import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { productAPI, contactAPI, orderAPI, serviceAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  PlusIcon, PencilIcon, TrashIcon,
  BellIcon, EnvelopeIcon, ShoppingCartIcon,
  XMarkIcon, ChartBarIcon, CogIcon, WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';

const TABS = ['Analytics', 'Products', 'Services', 'Contacts', 'Orders'];

const SERVICE_CATEGORIES = [
  'IT Consultation',
  'Network Installation',
  'Support',
  'Web Development',
  'Mobile Development',
  'Other',
];

const SERVICE_ICONS = {
  'IT Consultation':    '💼',
  'Network Installation': '🌐',
  'Support':            '🛠️',
  'Web Development':    '💻',
  'Mobile Development': '📱',
  'Other':              '⚙️',
};

// ── helpers ───────────────────────────────────────────────
const statusColor = (s) => ({
  pending:    'bg-yellow-100 text-yellow-800',
  contacted:  'bg-blue-100 text-blue-800',
  processing: 'bg-purple-100 text-purple-800',
  completed:  'bg-green-100 text-green-800',
  cancelled:  'bg-red-100 text-red-800',
  new:        'bg-orange-100 text-orange-800',
  read:       'bg-gray-100 text-gray-700',
  replied:    'bg-green-100 text-green-800',
}[s] || 'bg-gray-100 text-gray-700');

// ── StatCard ──────────────────────────────────────────────
const StatCard = ({ label, value, icon, color, onClick }) => (
  <div onClick={onClick}
    className={`bg-white rounded-2xl p-6 shadow cursor-pointer hover:shadow-lg transition flex items-center gap-4 ${onClick ? 'hover:ring-2 hover:ring-gtg-cyan-300' : ''}`}>
    <div className={`text-4xl`}>{icon}</div>
    <div>
      <p className={`text-3xl font-bold ${color}`}>{value}</p>
      <p className="text-gray-500 text-sm">{label}</p>
    </div>
  </div>
);

// ── MiniBar chart (pure CSS) ──────────────────────────────
const MiniBar = ({ data, color = 'bg-gtg-cyan-500' }) => {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div className="flex items-end gap-1 h-20">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div className={`${color} rounded-sm w-full transition-all`}
            style={{ height: `${(d.value / max) * 64}px` }} title={`${d.label}: ${d.value}`} />
          <span className="text-xs text-gray-400 truncate w-full text-center">{d.label}</span>
        </div>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
export default function Dashboard() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('Analytics');

  // ── data states ───────────────────────────────────────
  const [products,  setProducts]  = useState([]);
  const [services,  setServices]  = useState([]);
  const [contacts,  setContacts]  = useState([]);
  const [orders,    setOrders]    = useState([]);

  const [newContacts, setNewContacts] = useState(0);
  const [newOrders,   setNewOrders]   = useState(0);

  // ── loading ───────────────────────────────────────────
  const [loadingP, setLoadingP] = useState(true);
  const [loadingS, setLoadingS] = useState(true);
  const [loadingC, setLoadingC] = useState(true);
  const [loadingO, setLoadingO] = useState(true);

  // ── product form ──────────────────────────────────────
  const [showProductForm,  setShowProductForm]  = useState(false);
  const [editingProduct,   setEditingProduct]   = useState(null);
  const [imageMode,        setImageMode]        = useState('emoji');
  const [uploadPreview,    setUploadPreview]    = useState(null);
  const fileRef = useRef();
  const [pForm, setPForm] = useState({
    name:'', category:'Laptops', description:'', image:'📦',
    imageUrl:'', brand:'', specifications:'', featured:false,
  });

  // ── service form ──────────────────────────────────────
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService,  setEditingService]  = useState(null);
  const [sForm, setSForm] = useState({
    title:'', category:'IT Consultation', description:'',
    features:'', active:true, featured:false,
  });

  // ── contact / order detail ────────────────────────────
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedOrder,   setSelectedOrder]   = useState(null);

  const PRODUCT_CATS = ['Security Cameras','Laptops','PCs','Servers','Switches','Racks','Printers','Other'];

  // ── mount + poll ──────────────────────────────────────
  useEffect(() => {
    loadAll();
    const iv = setInterval(() => { loadContacts(true); loadOrders(true); }, 30000);
    return () => clearInterval(iv);
  }, []);

  const loadAll = () => { loadProducts(); loadServices(); loadContacts(); loadOrders(); };

  // ── loaders ───────────────────────────────────────────
  const loadProducts = async () => {
    try { setLoadingP(true); const r = await productAPI.getAll(); setProducts(r.data.data.products); }
    catch { toast.error('Failed to load products'); } finally { setLoadingP(false); }
  };

  const loadServices = async () => {
    try { setLoadingS(true); const r = await serviceAPI.getAll(); setServices(r.data.data.services); }
    catch { toast.error('Failed to load services'); } finally { setLoadingS(false); }
  };

  const loadContacts = async (silent=false) => {
    try {
      if (!silent) setLoadingC(true);
      const r = await contactAPI.getAll(); const all = r.data.data.contacts;
      setContacts(all); setNewContacts(all.filter(c=>c.status==='new').length);
    } catch { if(!silent) toast.error('Failed to load contacts'); }
    finally { if(!silent) setLoadingC(false); }
  };

  const loadOrders = async (silent=false) => {
    try {
      if (!silent) setLoadingO(true);
      const r = await orderAPI.getAll(); const all = r.data.data.orders;
      setOrders(all); setNewOrders(all.filter(o=>o.status==='pending').length);
    } catch { if(!silent) toast.error('Failed to load orders'); }
    finally { if(!silent) setLoadingO(false); }
  };

  // ── analytics derived data ────────────────────────────
  const ordersByStatus = ['pending','contacted','processing','completed','cancelled'].map(s => ({
    label: s, value: orders.filter(o=>o.status===s).length
  }));
  const contactsByStatus = ['new','read','replied','archived'].map(s => ({
    label: s, value: contacts.filter(c=>c.status===s).length
  }));
  const productsByCategory = PRODUCT_CATS.map(c => ({
    label: c.split(' ')[0], value: products.filter(p=>p.category===c).length
  })).filter(d=>d.value>0);
  const servicesByCategory = SERVICE_CATEGORIES.map(c => ({
    label: c.split(' ')[0], value: services.filter(s=>s.category===c).length
  })).filter(d=>d.value>0);

  // monthly orders (last 6 months)
  const monthlyOrders = Array.from({length:6},(_,i)=>{
    const d = new Date(); d.setMonth(d.getMonth()-5+i);
    return {
      label: d.toLocaleString('default',{month:'short'}),
      value: orders.filter(o=>{
        const od=new Date(o.createdAt);
        return od.getMonth()===d.getMonth() && od.getFullYear()===d.getFullYear();
      }).length
    };
  });

  // ── product CRUD ──────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => { setUploadPreview(reader.result); setPForm(f=>({...f,image:reader.result})); };
    reader.readAsDataURL(file);
  };

  const submitProduct = async (e) => {
    e.preventDefault();
    // Determine image value:
    // - upload mode: use the new base64 if user picked a file, else keep original
    // - url mode: use the url field
    // - emoji mode: use the emoji field
    let imageValue;
    if (imageMode === 'upload') {
      // Only use new upload if user actually picked a file (uploadPreview set)
      imageValue = uploadPreview ? pForm.image : (editingProduct?.image || '📦');
    } else if (imageMode === 'url') {
      imageValue = pForm.imageUrl || (editingProduct?.image || '📦');
    } else {
      // Emoji mode: never send a base64 or URL, only short emoji strings
      const raw = pForm.image || '📦';
      imageValue = (raw.startsWith('data:') || raw.startsWith('http')) ? '📦' : raw;
    }

    // Safety guard for base64 images > 2MB
    if (imageValue && imageValue.startsWith('data:') && imageValue.length > 2000000) {
      toast.error('Image is too large. Please use a URL link instead.');
      return;
    }

    const data = {
      name: pForm.name,
      category: pForm.category,
      description: pForm.description,
      brand: pForm.brand,
      image: imageValue,
      specifications: pForm.specifications.split(',').map(s=>s.trim()).filter(Boolean),
      featured: pForm.featured,
    };

    try {
      if (editingProduct) { await productAPI.update(editingProduct._id, data); toast.success('Product updated'); }
      else { await productAPI.create(data); toast.success('Product created'); }
      resetProductForm(); loadProducts();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to save product';
      toast.error(msg);
    }
  };

  const resetProductForm = () => {
    setShowProductForm(false); setEditingProduct(null); setImageMode('emoji'); setUploadPreview(null);
    setPForm({name:'',category:'Laptops',description:'',image:'📦',imageUrl:'',brand:'',specifications:'',featured:false});
  };

  const editProduct = (p) => {
    setEditingProduct(p);
    setUploadPreview(null); // always clear upload preview

    const isBase64 = p.image?.startsWith('data:');
    const isUrl    = p.image?.startsWith('http');
    const isEmoji  = p.image && !isBase64 && !isUrl;

    // Pick the right mode based on existing image type
    if (isUrl) {
      setImageMode('url');
    } else {
      setImageMode('emoji'); // for emoji OR base64, default to emoji (strips base64)
    }

    setPForm({
      name:           p.name,
      category:       p.category,
      description:    p.description,
      image:          isEmoji ? p.image : '📦',   // only keep if it's an actual emoji
      imageUrl:       isUrl   ? p.image : '',
      brand:          p.brand || '',
      specifications: p.specifications?.join(', ') || '',
      featured:       p.featured || false,
    });
    setShowProductForm(true);
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await productAPI.delete(id); toast.success('Deleted'); loadProducts(); }
    catch { toast.error('Failed to delete'); }
  };

  // ── service CRUD ──────────────────────────────────────
  const submitService = async (e) => {
    e.preventDefault();
    const data = { ...sForm, features: sForm.features.split(',').map(s=>s.trim()).filter(Boolean) };
    try {
      if (editingService) { await serviceAPI.update(editingService._id, data); toast.success('Service updated'); }
      else { await serviceAPI.create(data); toast.success('Service created'); }
      resetServiceForm(); loadServices();
    } catch { toast.error('Failed to save service'); }
  };

  const resetServiceForm = () => {
    setShowServiceForm(false); setEditingService(null);
    setSForm({title:'',category:'IT Consultation',description:'',features:'',active:true,featured:false});
  };

  const editService = (s) => {
    setEditingService(s);
    setSForm({title:s.title,category:s.category,description:s.description,features:s.features?.join(', ')||'',active:s.active,featured:s.featured});
    setShowServiceForm(true);
  };

  const deleteService = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try { await serviceAPI.delete(id); toast.success('Deleted'); loadServices(); }
    catch { toast.error('Failed to delete'); }
  };

  // ── contact / order actions ───────────────────────────
  const openContact = async (c) => {
    setSelectedContact(c);
    if (c.status==='new') { try { await contactAPI.updateStatus(c._id,{status:'read'}); loadContacts(true); } catch {} }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Delete message?')) return;
    try { await contactAPI.delete(id); toast.success('Deleted'); setSelectedContact(null); loadContacts(true); }
    catch { toast.error('Failed'); }
  };

  const updateOrderStatus = async (id, status) => {
    try {
      await orderAPI.updateStatus(id,{status}); toast.success('Status updated'); loadOrders(true);
      if (selectedOrder?._id===id) setSelectedOrder(p=>({...p,status}));
    } catch { toast.error('Failed'); }
  };

  // ── render ────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-500 mt-1">Welcome back, {user?.name}</p>
          </div>
          <button onClick={logout} className="px-6 py-3 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition">
            Logout
          </button>
        </div>

        {/* Top stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total Products"  value={products.length}  icon="📦" color="text-gtg-blue-600"  onClick={() => setActiveTab('Products')} />
          <StatCard label="Services"        value={services.length}  icon="⚙️" color="text-gtg-green-600" onClick={() => setActiveTab('Services')} />
          <StatCard label={`Messages ${newContacts>0?`(${newContacts} new)`:''}`} value={contacts.length} icon="✉️" color="text-gtg-cyan-600" onClick={() => setActiveTab('Contacts')} />
          <StatCard label={`Orders ${newOrders>0?`(${newOrders} pending)`:''}`}   value={orders.length}   icon="🛒" color="text-purple-600"    onClick={() => setActiveTab('Orders')} />
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-5 py-2 rounded-lg font-semibold transition ${
                activeTab===tab ? 'bg-gradient-to-r from-gtg-blue-500 to-gtg-cyan-500 text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}>
              {tab}
              {tab==='Contacts' && newContacts>0 && <span className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{newContacts}</span>}
              {tab==='Orders'   && newOrders>0   && <span className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{newOrders}</span>}
            </button>
          ))}
        </div>

        {/* ══ ANALYTICS TAB ══════════════════════════════ */}
        {activeTab==='Analytics' && (
          <div className="space-y-6">
            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl p-6 shadow text-center">
                <p className="text-3xl font-bold text-gtg-blue-600">{orders.filter(o=>o.status==='completed').length}</p>
                <p className="text-gray-500 text-sm mt-1">Completed Orders</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow text-center">
                <p className="text-3xl font-bold text-yellow-500">{orders.filter(o=>o.status==='pending').length}</p>
                <p className="text-gray-500 text-sm mt-1">Pending Orders</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow text-center">
                <p className="text-3xl font-bold text-orange-500">{contacts.filter(c=>c.status==='new').length}</p>
                <p className="text-gray-500 text-sm mt-1">Unread Messages</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow text-center">
                <p className="text-3xl font-bold text-gtg-green-600">{products.filter(p=>p.featured).length}</p>
                <p className="text-gray-500 text-sm mt-1">Featured Products</p>
              </div>
            </div>

            {/* Charts row */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 shadow">
                <h3 className="font-bold text-gray-900 mb-4">📈 Monthly Orders (Last 6 Months)</h3>
                <MiniBar data={monthlyOrders} color="bg-gtg-blue-500" />
              </div>
              <div className="bg-white rounded-2xl p-6 shadow">
                <h3 className="font-bold text-gray-900 mb-4">📊 Orders by Status</h3>
                <MiniBar data={ordersByStatus} color="bg-gtg-cyan-500" />
              </div>
              <div className="bg-white rounded-2xl p-6 shadow">
                <h3 className="font-bold text-gray-900 mb-4">📦 Products by Category</h3>
                {productsByCategory.length > 0 ? <MiniBar data={productsByCategory} color="bg-gtg-green-500" /> : <p className="text-gray-400 text-sm">No products yet</p>}
              </div>
              <div className="bg-white rounded-2xl p-6 shadow">
                <h3 className="font-bold text-gray-900 mb-4">✉️ Messages by Status</h3>
                <MiniBar data={contactsByStatus} color="bg-purple-400" />
              </div>
            </div>

            {/* Recent activity */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 shadow">
                <h3 className="font-bold text-gray-900 mb-4">🕐 Recent Orders</h3>
                {orders.slice(0,5).map(o => (
                  <div key={o._id} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <p className="font-medium text-sm text-gray-800">{o.customer?.name}</p>
                      <p className="text-xs text-gray-400">{o.orderNumber}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColor(o.status)}`}>{o.status}</span>
                  </div>
                ))}
                {orders.length===0 && <p className="text-gray-400 text-sm">No orders yet</p>}
              </div>
              <div className="bg-white rounded-2xl p-6 shadow">
                <h3 className="font-bold text-gray-900 mb-4">✉️ Recent Messages</h3>
                {contacts.slice(0,5).map(c => (
                  <div key={c._id} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <p className="font-medium text-sm text-gray-800 flex items-center gap-1">
                        {c.name} {c.status==='new' && <span className="w-2 h-2 bg-red-500 rounded-full inline-block"/>}
                      </p>
                      <p className="text-xs text-gray-400 truncate max-w-[180px]">{c.message}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColor(c.status)}`}>{c.status}</span>
                  </div>
                ))}
                {contacts.length===0 && <p className="text-gray-400 text-sm">No messages yet</p>}
              </div>
            </div>
          </div>
        )}

        {/* ══ PRODUCTS TAB ═══════════════════════════════ */}
        {activeTab==='Products' && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Product Management</h2>
              <button onClick={() => { resetProductForm(); setShowProductForm(true); }}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gtg-blue-500 to-gtg-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg transition">
                <PlusIcon className="w-5 h-5"/> Add Product
              </button>
            </div>

            {showProductForm && (
              <motion.form initial={{opacity:0,height:0}} animate={{opacity:1,height:'auto'}}
                onSubmit={submitProduct} className="mb-8 p-6 bg-gray-50 rounded-xl">
                <h3 className="text-xl font-bold mb-4">{editingProduct ? 'Edit Product' : 'New Product'}</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input type="text" value={pForm.name} required onChange={e=>setPForm({...pForm,name:e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gtg-cyan-500"/>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                    <select value={pForm.category} onChange={e=>setPForm({...pForm,category:e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gtg-cyan-500">
                      {PRODUCT_CATS.map(c=><option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                    <input type="text" value={pForm.brand} onChange={e=>setPForm({...pForm,brand:e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gtg-cyan-500"/>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                    <div className="flex gap-2 mb-2">
                      {['emoji','url','upload'].map(m=>(
                        <button key={m} type="button" onClick={()=>setImageMode(m)}
                          className={`px-3 py-1 rounded text-sm font-medium ${imageMode===m?'bg-gtg-cyan-500 text-white':'bg-gray-200 text-gray-600'}`}>
                          {m==='emoji'?'😀 Emoji':m==='url'?'🔗 URL':'📁 Upload'}
                        </button>
                      ))}
                    </div>
                    {imageMode==='emoji' && <input type="text" value={pForm.image} onChange={e=>setPForm({...pForm,image:e.target.value})} placeholder="📦" className="w-full px-4 py-2 border border-gray-300 rounded-lg"/>}
                    {imageMode==='url'   && <input type="url"  value={pForm.imageUrl} onChange={e=>setPForm({...pForm,imageUrl:e.target.value})} placeholder="https://..." className="w-full px-4 py-2 border border-gray-300 rounded-lg"/>}
                    {imageMode==='upload' && (
                      <>
                        <input type="file" accept="image/*" ref={fileRef} onChange={handleFileChange} className="hidden"/>
                        <button type="button" onClick={()=>fileRef.current.click()}
                          className="w-full px-4 py-2 border-2 border-dashed border-gtg-cyan-400 rounded-lg text-gtg-cyan-600 hover:bg-gtg-cyan-50 text-sm transition">
                          Click to upload
                        </button>
                        {uploadPreview && <img src={uploadPreview} alt="preview" className="mt-2 h-16 rounded object-cover"/>}
                      </>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                    <textarea value={pForm.description} required rows={3} onChange={e=>setPForm({...pForm,description:e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gtg-cyan-500"/>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Specifications (comma-separated)</label>
                    <input type="text" value={pForm.specifications} onChange={e=>setPForm({...pForm,specifications:e.target.value})}
                      placeholder="Spec 1, Spec 2" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gtg-cyan-500"/>
                  </div>
                  <div className="md:col-span-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={pForm.featured} onChange={e=>setPForm({...pForm,featured:e.target.checked})} className="w-4 h-4"/>
                      <span className="text-sm font-medium text-gray-700">Featured Product</span>
                    </label>
                  </div>
                </div>
                <div className="flex gap-4 mt-6">
                  <button type="submit" className="px-6 py-3 bg-gradient-to-r from-gtg-blue-500 to-gtg-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg transition">
                    {editingProduct ? 'Update' : 'Add Product'}
                  </button>
                  <button type="button" onClick={resetProductForm} className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-400 transition">Cancel</button>
                </div>
              </motion.form>
            )}

            {loadingP ? <div className="flex justify-center py-12"><div className="loading-spinner"/></div> : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map(p=>(
                  <div key={p._id} className="border border-gray-200 rounded-xl p-5 hover:shadow-lg transition">
                    <div className="h-14 w-14 mb-3 flex items-center justify-center">
                      {p.image?.startsWith('data:') || p.image?.startsWith('http')
                        ? <img src={p.image} alt={p.name} className="h-14 w-14 object-cover rounded-lg"/>
                        : <span className="text-4xl">{p.image}</span>}
                    </div>
                    <span className="text-xs text-gtg-cyan-600 font-semibold">{p.category}</span>
                    <h3 className="text-base font-bold text-gray-900 mt-1 mb-1">{p.name}</h3>
                    <p className="text-gray-500 text-sm mb-4 line-clamp-2">{p.description}</p>
                    <div className="flex gap-2">
                      <button onClick={()=>editProduct(p)} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gtg-blue-500 text-white rounded-lg text-sm font-semibold hover:bg-gtg-blue-600 transition">
                        <PencilIcon className="w-4 h-4"/> Edit
                      </button>
                      <button onClick={()=>deleteProduct(p._id)} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 transition">
                        <TrashIcon className="w-4 h-4"/> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══ SERVICES TAB ═══════════════════════════════ */}
        {activeTab==='Services' && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Services Management</h2>
              <button onClick={()=>{resetServiceForm();setShowServiceForm(true);}}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gtg-blue-500 to-gtg-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg transition">
                <PlusIcon className="w-5 h-5"/> Add Service
              </button>
            </div>

            {showServiceForm && (
              <motion.form initial={{opacity:0,height:0}} animate={{opacity:1,height:'auto'}}
                onSubmit={submitService} className="mb-8 p-6 bg-gray-50 rounded-xl">
                <h3 className="text-xl font-bold mb-4">{editingService ? 'Edit Service' : 'New Service'}</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input type="text" value={sForm.title} required onChange={e=>setSForm({...sForm,title:e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gtg-cyan-500"/>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                    <select value={sForm.category} onChange={e=>setSForm({...sForm,category:e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gtg-cyan-500">
                      {SERVICE_CATEGORIES.map(c=><option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                    <textarea value={sForm.description} required rows={3} onChange={e=>setSForm({...sForm,description:e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gtg-cyan-500"/>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Key Features (comma-separated)</label>
                    <input type="text" value={sForm.features} onChange={e=>setSForm({...sForm,features:e.target.value})}
                      placeholder="Feature 1, Feature 2, Feature 3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gtg-cyan-500"/>
                  </div>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={sForm.active} onChange={e=>setSForm({...sForm,active:e.target.checked})} className="w-4 h-4"/>
                      <span className="text-sm font-medium text-gray-700">Active (visible on site)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={sForm.featured} onChange={e=>setSForm({...sForm,featured:e.target.checked})} className="w-4 h-4"/>
                      <span className="text-sm font-medium text-gray-700">Featured</span>
                    </label>
                  </div>
                </div>
                <div className="flex gap-4 mt-6">
                  <button type="submit" className="px-6 py-3 bg-gradient-to-r from-gtg-blue-500 to-gtg-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg transition">
                    {editingService ? 'Update' : 'Add Service'}
                  </button>
                  <button type="button" onClick={resetServiceForm} className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-400 transition">Cancel</button>
                </div>
              </motion.form>
            )}

            {loadingS ? <div className="flex justify-center py-12"><div className="loading-spinner"/></div> : (
              <div className="grid md:grid-cols-2 gap-6">
                {services.map(s=>(
                  <div key={s._id} className={`border rounded-xl p-6 transition hover:shadow-lg ${s.active?'border-gray-200':'border-dashed border-gray-300 opacity-60'}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{SERVICE_ICONS[s.category]||'⚙️'}</span>
                        <div>
                          <h3 className="font-bold text-gray-900">{s.title}</h3>
                          <span className="text-xs text-gtg-cyan-600 font-medium">{s.category}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!s.active && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Hidden</span>}
                        {s.featured && <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">Featured</span>}
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">{s.description}</p>
                    {s.features?.length>0 && (
                      <ul className="text-xs text-gray-500 mb-4 space-y-1">
                        {s.features.slice(0,3).map((f,i)=>(
                          <li key={i} className="flex items-center gap-1"><span className="w-1.5 h-1.5 bg-gtg-cyan-500 rounded-full"/>{f}</li>
                        ))}
                      </ul>
                    )}
                    <div className="flex gap-2">
                      <button onClick={()=>editService(s)} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gtg-blue-500 text-white rounded-lg text-sm font-semibold hover:bg-gtg-blue-600 transition">
                        <PencilIcon className="w-4 h-4"/> Edit
                      </button>
                      <button onClick={()=>deleteService(s._id)} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 transition">
                        <TrashIcon className="w-4 h-4"/> Delete
                      </button>
                    </div>
                  </div>
                ))}
                {services.length===0 && <p className="text-gray-400 col-span-2 text-center py-12">No services yet. Add your first one!</p>}
              </div>
            )}
          </div>
        )}

        {/* ══ CONTACTS TAB ═══════════════════════════════ */}
        {activeTab==='Contacts' && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Messages</h2>
            {loadingC ? <div className="flex justify-center py-12"><div className="loading-spinner"/></div>
            : contacts.length===0 ? <p className="text-center text-gray-400 py-12">No messages yet.</p>
            : (
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
                  {contacts.map(c=>(
                    <div key={c._id} onClick={()=>openContact(c)}
                      className={`p-4 rounded-xl border cursor-pointer transition hover:shadow-md ${selectedContact?._id===c._id?'border-gtg-cyan-500 bg-gtg-cyan-50':'border-gray-200'}`}>
                      <div className="flex justify-between items-start">
                        <p className="font-semibold text-gray-900 flex items-center gap-2">
                          {c.name} {c.status==='new' && <span className="w-2 h-2 bg-red-500 rounded-full"/>}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(c.status)}`}>{c.status}</span>
                      </div>
                      <p className="text-sm text-gray-500">{c.email}</p>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{c.message}</p>
                      <p className="text-xs text-gray-400 mt-1">{new Date(c.createdAt).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
                {selectedContact ? (
                  <div className="border border-gray-200 rounded-xl p-6">
                    <div className="flex justify-between mb-4">
                      <h3 className="text-xl font-bold text-gray-900">{selectedContact.name}</h3>
                      <button onClick={()=>setSelectedContact(null)}><XMarkIcon className="w-5 h-5 text-gray-400"/></button>
                    </div>
                    <div className="space-y-2 text-sm mb-4">
                      <div><span className="font-medium">Email:</span> <a href={`mailto:${selectedContact.email}`} className="text-gtg-cyan-600 hover:underline">{selectedContact.email}</a></div>
                      {selectedContact.phone   && <div><span className="font-medium">Phone:</span> {selectedContact.phone}</div>}
                      {selectedContact.company && <div><span className="font-medium">Company:</span> {selectedContact.company}</div>}
                      {selectedContact.subject && <div><span className="font-medium">Subject:</span> {selectedContact.subject}</div>}
                      <div><span className="font-medium">Date:</span> {new Date(selectedContact.createdAt).toLocaleString()}</div>
                      <div><span className="font-medium">Message:</span>
                        <p className="mt-1 p-3 bg-gray-50 rounded-lg whitespace-pre-wrap">{selectedContact.message}</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <a href={`mailto:${selectedContact.email}`} className="flex-1 text-center px-4 py-2 bg-gtg-cyan-500 text-white rounded-lg text-sm font-semibold hover:bg-gtg-cyan-600 transition">Reply by Email</a>
                      <button onClick={()=>deleteContact(selectedContact._id)} className="px-4 py-2 bg-red-100 text-red-600 rounded-lg text-sm font-semibold hover:bg-red-200 transition">Delete</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center border border-dashed border-gray-200 rounded-xl h-48">
                    <p className="text-gray-400">Select a message to view</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ══ ORDERS TAB ═════════════════════════════════ */}
        {activeTab==='Orders' && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Product Requests / Orders</h2>
            {loadingO ? <div className="flex justify-center py-12"><div className="loading-spinner"/></div>
            : orders.length===0 ? <p className="text-center text-gray-400 py-12">No orders yet.</p>
            : (
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
                  {orders.map(o=>(
                    <div key={o._id} onClick={()=>setSelectedOrder(o)}
                      className={`p-4 rounded-xl border cursor-pointer transition hover:shadow-md ${selectedOrder?._id===o._id?'border-gtg-blue-500 bg-gtg-blue-50':'border-gray-200'}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-gray-900">{o.customer?.name}</p>
                          <p className="text-xs text-gray-400">{o.orderNumber}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(o.status)}`}>{o.status}</span>
                      </div>
                      {o.customer?.company && <p className="text-sm text-gray-500 mt-1">{o.customer.company}</p>}
                      <p className="text-xs text-gray-400 mt-1">{new Date(o.createdAt).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
                {selectedOrder ? (
                  <div className="border border-gray-200 rounded-xl p-6">
                    <div className="flex justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{selectedOrder.customer?.name}</h3>
                        <p className="text-xs text-gray-400">{selectedOrder.orderNumber}</p>
                      </div>
                      <button onClick={()=>setSelectedOrder(null)}><XMarkIcon className="w-5 h-5 text-gray-400"/></button>
                    </div>
                    <div className="space-y-2 text-sm mb-4">
                      <div><span className="font-medium">Email:</span> <a href={`mailto:${selectedOrder.customer?.email}`} className="text-gtg-cyan-600 hover:underline">{selectedOrder.customer?.email}</a></div>
                      <div><span className="font-medium">Phone:</span> {selectedOrder.customer?.phone}</div>
                      {selectedOrder.customer?.company && <div><span className="font-medium">Company:</span> {selectedOrder.customer.company}</div>}
                      {selectedOrder.notes && <div><span className="font-medium">Notes:</span> {selectedOrder.notes}</div>}
                    </div>
                    {selectedOrder.products?.length>0 && (
                      <div className="mb-4">
                        <p className="font-medium text-gray-700 mb-2">Requested Products:</p>
                        {selectedOrder.products.map((p,i)=>(
                          <div key={i} className="bg-gray-50 rounded-lg p-3 mb-2 text-sm">
                            <p className="font-medium">{p.product?.name||'Product'}</p>
                            <p className="text-gray-500">Qty: {p.quantity}</p>
                            {p.specifications && <p className="text-gray-500">Notes: {p.specifications}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="mb-4">
                      <p className="font-medium text-gray-700 mb-2">Update Status:</p>
                      <div className="flex flex-wrap gap-2">
                        {['pending','contacted','processing','completed','cancelled'].map(s=>(
                          <button key={s} onClick={()=>updateOrderStatus(selectedOrder._id,s)}
                            className={`px-3 py-1 rounded-lg text-xs font-semibold capitalize transition ${selectedOrder.status===s?statusColor(s)+' ring-2 ring-offset-1 ring-gray-300':'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                    <a href={`mailto:${selectedOrder.customer?.email}`}
                      className="block text-center px-4 py-2 bg-gtg-blue-500 text-white rounded-lg text-sm font-semibold hover:bg-gtg-blue-600 transition">
                      Reply by Email
                    </a>
                  </div>
                ) : (
                  <div className="flex items-center justify-center border border-dashed border-gray-200 rounded-xl h-48">
                    <p className="text-gray-400">Select an order to view details</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}